import React, { useContext, useEffect, useState } from "react";
import { ThemeContext } from "../../hooks/ThemeContext";
import { UserContext } from "../../hooks/UserContext";
import { FaDiscord, FaTiktok } from "react-icons/fa";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { api } from "../../services/baseUrl";

const primaryLinks = [
  { label: "Créations", to: "/creations" },
  { label: "Boutique", to: "/shop" },
  { label: "Nos services", to: "/services" },
  { label: "Contact", to: "/contact" },
];

const secondaryLinks = [
  { label: "Accueil", to: "/" },
  { label: "Espace client", to: "/espace-client" },
  { label: "Devenir créateur", to: "/devenir-createur" },
];

const socialLinks = [
  { label: "Discord", href: "https://discord.com/invite/4XbU25c87e", Icon: FaDiscord },
  { label: "TikTok", href: "https://www.tiktok.com/@krayne_off", Icon: FaTiktok },
];

const Footer = () => {
  const { theme } = useContext(ThemeContext);
  const { user } = useContext(UserContext);
  const [newsletterEnabled, setNewsletterEnabled] = useState(false);
  const [newsletterLoading, setNewsletterLoading] = useState(false);

  const isDark = theme === "dark";

  useEffect(() => {
    let isMounted = true;

    if (!user?.email) {
      setNewsletterEnabled(false);
      return () => {
        isMounted = false;
      };
    }

    api
      .get("/newsletter/status")
      .then((response) => {
        if (!isMounted) return;
        setNewsletterEnabled(Boolean(response.data?.subscribed));
      })
      .catch(() => {
        if (!isMounted) return;
        setNewsletterEnabled(false);
      });

    return () => {
      isMounted = false;
    };
  }, [user?.email]);

  const handleNewsletterToggle = async (e) => {
    e.preventDefault();

    if (!user?.email) {
      toast.error("Connectez-vous pour activer la newsletter.");
      return;
    }

    try {
      setNewsletterLoading(true);

      if (newsletterEnabled) {
        await api.post("/newsletter/unsubscribe");
        setNewsletterEnabled(false);
        toast.success("Alertes désactivées.");
      } else {
        await api.post("/newsletter/subscribe", { email: user.email });
        setNewsletterEnabled(true);
        toast.success("Alertes activées.");
      }
    } catch (error) {
      const message = error.response?.data?.message || "Impossible de mettre à jour vos alertes.";
      toast.error(message);
    } finally {
      setNewsletterLoading(false);
    }
  };

  const newsletterButtonClass = newsletterLoading
    ? "bg-gray-400 cursor-not-allowed"
    : isDark
      ? "bg-rose-500 hover:bg-rose-600"
      : "bg-rose-600 hover:bg-rose-700";

  const newsletterButtonLabel = newsletterLoading
    ? "Mise à jour..."
    : newsletterEnabled
      ? "Désactiver les alertes"
      : "Activer les alertes";

  return (
    <footer
      className={`${isDark ? "bg-[#09111c] text-white" : "bg-[#fcf8f6] text-gray-900"} w-full relative overflow-hidden mt-32 pb-10`}
      style={{ letterSpacing: 0.1, zIndex: 10 }}
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className={`absolute -top-32 -right-32 h-[28rem] w-[28rem] rounded-full blur-[120px] ${isDark ? "bg-rose-500/14" : "bg-rose-300/20"}`} />
        <div className={`absolute -bottom-36 -left-32 h-[28rem] w-[28rem] rounded-full blur-[120px] ${isDark ? "bg-purple-500/10" : "bg-white/80"}`} />
        <div className={`absolute inset-0 ${isDark ? "bg-[radial-gradient(circle_at_top,_rgba(244,63,94,0.12),_transparent_35%),radial-gradient(circle_at_80%_20%,_rgba(168,85,247,0.08),_transparent_25%)]" : "bg-[radial-gradient(circle_at_top,_rgba(244,63,94,0.06),_transparent_60%)]"}`} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 md:px-6 pt-32">
        <div className={`relative overflow-hidden border p-6 md:p-8 lg:p-10 ${isDark ? 'border-white/10 bg-gradient-to-br from-rose-900/20 via-slate-900 to-blue-950 shadow-[0_35px_120px_rgba(0,0,0,0.22)]' : 'border-rose-100/80 bg-white/95 shadow-[0_24px_70px_rgba(15,23,42,0.08)]'}`}>
          <div className="absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-rose-400/70 to-transparent" />

          <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
            <div className={`border p-6 md:p-8 shadow-xl ${isDark ? 'border-white/10 bg-white/5' : 'border-rose-100/80 bg-white'}`}>
              <div className="flex items-center gap-4 mb-6">
                <span className="h-10 w-1 rounded bg-rose-500/80 block" />
                <div>
                  <p className="uppercase tracking-[0.3em] text-xs font-bold text-rose-500">Krayne Agency</p>
                  <p className={`${isDark ? 'text-white/50' : 'text-gray-500'} text-xs mt-1`}>Studio digital premium</p>
                </div>
              </div>

              <Link to="/" className="inline-flex items-center gap-3 group">
                <span className={`inline-flex items-center gap-3 rounded-2xl px-4 py-3 shadow-xl ${isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-rose-100'}`}>
                  <span className="h-2.5 w-2.5 rounded-full bg-rose-500" />
                  <span
                    className={`text-3xl md:text-4xl font-extrabold tracking-tight ${isDark ? 'text-white group-hover:text-rose-300' : 'text-gray-900 group-hover:text-rose-600'} transition-colors`}
                    style={{ fontFamily: "'Space Grotesk', 'Plus Jakarta Sans', sans-serif", letterSpacing: '-0.04em' }}
                  >
                    KRAYNE <span className="text-rose-500">AGENCY</span>
                  </span>
                </span>
              </Link>

              <div className="mt-8 grid gap-4 sm:grid-cols-3">
                {[
                  { label: 'Design premium', value: 'UX cohérente' },
                  { label: 'Réponse rapide', value: 'Support clair' },
                  { label: 'Créations digitales', value: 'Livraison rapide' },
                ].map((item) => (
                  <div key={item.label} className={`border p-4 shadow-xl ${isDark ? 'border-white/10 bg-white/5' : 'border-rose-100 bg-[#fff9f7]'}`}>
                    <p className="text-sm font-semibold mb-1">{item.label}</p>
                    <p className={`${isDark ? 'text-white/60' : 'text-gray-600'} text-sm`}>{item.value}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Link
                  to="/contact"
                  className={`px-7 py-3.5 text-sm font-semibold transition shadow-xl ${
                    isDark ? 'bg-rose-500 text-white hover:bg-rose-600' : 'bg-rose-600 text-white hover:bg-rose-700'
                  }`}
                >
                  Demander un devis
                </Link>
                <Link
                  to="/services"
                  className={`px-7 py-3.5 text-sm font-semibold transition border shadow-xl ${
                    isDark ? 'border-white/10 bg-white/5 text-white hover:bg-white/10' : 'border-rose-100 bg-white text-gray-700 hover:bg-rose-50'
                  }`}
                >
                  Voir nos offres
                </Link>
                <div className="flex items-center gap-3">
                  {socialLinks.map(({ label, href, Icon }) => (
                    <a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      className={`h-12 w-12 flex items-center justify-center border shadow-xl transition ${
                        isDark ? 'border-white/10 bg-white/5 hover:bg-white/10' : 'border-rose-100 bg-white hover:bg-rose-50'
                      }`}
                      aria-label={label}
                    >
                      <Icon className={`${isDark ? 'text-white' : 'text-rose-600'} text-xl`} />
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid gap-6">
              <div className={`border p-6 md:p-8 shadow-xl ${isDark ? 'border-white/10 bg-white/5' : 'border-rose-100 bg-white'}`}>
                <div className="flex items-center gap-3 mb-5">
                  <span className="h-6 w-1 rounded bg-rose-500/80 block" />
                  <h4 className={`text-xs uppercase tracking-[0.3em] font-bold ${isDark ? 'text-white/50' : 'text-rose-500'}`}>Alertes promos</h4>
                </div>

                <p className={`${isDark ? 'text-white/70' : 'text-gray-700'} text-sm leading-7`}>
                  Un email clair quand une promo devient active. <span className="text-rose-500 font-semibold">Pas de spam, jamais.</span>
                </p>

                <form onSubmit={handleNewsletterToggle} className="mt-5 flex flex-col gap-3">
                  <div
                    className={`w-full px-4 py-3 border text-sm ${
                      isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-[#fff8f5] border-rose-100 text-gray-900'
                    }`}
                  >
                    {user?.email || 'Connectez-vous pour activer la newsletter'}
                  </div>

                  {user?.email ? (
                    <button
                      type="submit"
                      disabled={newsletterLoading}
                      className={`w-full px-5 py-3.5 font-semibold text-white transition shadow-xl ${newsletterButtonClass}`}
                    >
                      {newsletterButtonLabel}
                    </button>
                  ) : (
                    <Link
                      to="/login"
                      className={`w-full px-5 py-3.5 text-center font-semibold border shadow-xl ${
                        isDark ? 'bg-white/5 text-white hover:bg-white/10 border-white/10' : 'bg-white text-gray-800 border-rose-100 hover:bg-rose-50'
                      }`}
                    >
                      Se connecter
                    </Link>
                  )}
                </form>

                <p className={`mt-4 text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                  Désinscription en 1 clic depuis votre profil.
                </p>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <div className={`border p-6 shadow-xl ${isDark ? 'border-white/10 bg-white/5' : 'border-rose-100 bg-white'}`}>
                  <h4 className={`text-xs uppercase tracking-[0.3em] font-bold mb-4 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                    Navigation
                  </h4>
                  <div className="flex flex-col gap-3">
                    {primaryLinks.map((link) => (
                      <Link
                        key={link.to}
                        to={link.to}
                        className={`px-4 py-3 text-sm font-medium border transition ${
                          isDark ? 'border-white/10 bg-white/5 text-white/80 hover:bg-white/[0.08] hover:text-rose-300' : 'border-rose-100 bg-white text-gray-700 hover:text-rose-600 hover:bg-rose-50'
                        }`}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                </div>

                <div className={`border p-6 shadow-xl ${isDark ? 'border-white/10 bg-white/5' : 'border-rose-100 bg-white'}`}>
                  <h4 className={`text-xs uppercase tracking-[0.3em] font-bold mb-4 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                    Accès rapide
                  </h4>
                  <div className="flex flex-col gap-3">
                    {secondaryLinks.map((link) => (
                      <Link
                        key={link.to}
                        to={link.to}
                        className={`px-4 py-3 text-sm font-medium border transition ${
                          isDark ? 'border-white/10 bg-white/5 text-white/70 hover:bg-white/[0.08] hover:text-rose-300' : 'border-rose-100 bg-white text-gray-700 hover:text-rose-600 hover:bg-rose-50'
                        }`}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={`mt-6 border px-6 py-5 shadow-xl ${isDark ? 'border-white/10 bg-white/5' : 'border-rose-100/80 bg-white/90'}`}>
            <div className="flex flex-col md:flex-row items-center justify-between gap-5 text-sm">
              <div className="flex items-center gap-3">
                <span className="h-2 w-20 bg-rose-500/50 block" />
                <p className={`${isDark ? 'text-white/60' : 'text-gray-500'}`}>© 2026 Krayne Agency. Tous droits réservés.</p>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <Link to="/services" className={`${isDark ? 'border border-white/10 bg-white/5 text-white/70 hover:bg-white/[0.08] hover:text-rose-300' : 'border border-rose-100 bg-white text-gray-600 hover:text-rose-500'} px-4 py-2 transition`}>
                  Services
                </Link>
                <Link to="/contact" className={`${isDark ? 'border border-white/10 bg-white/5 text-white/70 hover:bg-white/[0.08] hover:text-rose-300' : 'border border-rose-100 bg-white text-gray-600 hover:text-rose-500'} px-4 py-2 transition`}>
                  Contact
                </Link>
                <Link to="/devenir-createur" className={`${isDark ? 'border border-white/10 bg-white/5 text-white/70 hover:bg-white/[0.08] hover:text-rose-300' : 'border border-rose-100 bg-white text-gray-600 hover:text-rose-500'} px-4 py-2 transition`}>
                  Devenir créateur
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
